<template>
  <section class="drop-card-shell d-none d-lg-inline-block">
    <div class="backdrop" @click="$emit('close')"></div>

    <div class="drop-card-panel work-sans" @click.stop @mouseleave="emitClose">
      <div class="drop-card-header">
        <div class="drop-card-header__top">
          <p class="drop-card-header__title">
            Choose from over {{ allDashboards.length }} available dashboards or create your own
          </p>

          <button
            type="button"
            class="drop-card-close"
            aria-label="Close dashboard selector"
            @click.stop="emitClose"
          >
            <b-icon icon="x"></b-icon>
          </button>
        </div>

        <div class="drop-card-search">
          <b-icon icon="search" class="drop-card-search__icon"></b-icon>
          <input
            v-model.trim="searchTerm"
            type="text"
            class="drop-card-search__input"
            placeholder="Search dashboards by name, category, or description..."
          />
        </div>
      </div>

      <div class="drop-card-filters">
        <button
          v-for="filter in categoryFilters"
          :key="filter.key"
          type="button"
          :class="[
            'drop-card-filter',
            { 'drop-card-filter--active': activeCategory === filter.key },
          ]"
          @click.stop="handleFilterClick(filter.key)"
        >
          <span class="drop-card-filter__label">{{ filter.label }}</span>
          <span class="drop-card-filter__count">{{ filter.count }}</span>
        </button>

        <button
          v-for="action in quickActions"
          :key="action.id"
          type="button"
          class="drop-card-filter drop-card-filter--cta"
          :title="action.description"
          :aria-label="`${action.title}: ${action.description}`"
          @click.stop="openDashboardCard(action)"
        >
          <span class="drop-card-filter__label">{{ action.title }}</span>
          <span class="drop-card-filter__count drop-card-filter__count--cta">&rarr;</span>
        </button>
      </div>

      <div class="drop-card-content">
        <div v-if="showCustomDashboardsLoading" class="drop-card-state">
          <b-spinner small variant="primary" class="mr-2"></b-spinner>
          <span>Loading custom dashboards...</span>
        </div>

        <div v-else-if="showCustomDashboardsError" class="drop-card-state drop-card-state--empty">
          <p class="drop-card-state__title">Custom dashboards are unavailable</p>
          <p class="drop-card-state__text">Please try the Custom Dashboards tab again.</p>
        </div>

        <div
          v-else-if="filteredDashboards.length === 0"
          class="drop-card-state drop-card-state--empty"
        >
          <p class="drop-card-state__title">No dashboards match your search</p>
          <p class="drop-card-state__text">Try another category or search term.</p>
        </div>

        <div v-else class="drop-card-grid">
          <article
            v-for="dashboard in filteredDashboards"
            :key="dashboard.id"
            :class="['drop-card-item', { 'drop-card-item--featured': dashboard.featured }]"
            role="button"
            tabindex="0"
            @click="openDashboardCard(dashboard)"
            @keyup.enter="openDashboardCard(dashboard)"
            @keyup.space.prevent="openDashboardCard(dashboard)"
          >
            <div class="drop-card-item__body">
              <h3 class="drop-card-item__title">{{ dashboard.title }}</h3>
              <p class="drop-card-item__description">{{ dashboard.description }}</p>
            </div>

            <div class="drop-card-item__footer">
              <span class="drop-card-item__tag">{{ dashboard.category }}</span>
              <span class="drop-card-item__arrow">&rarr;</span>
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { advancedSections } from '../../analytics/sections';

const CUSTOM_DASHBOARDS_CACHE_KEY = 'msdat-public-custom-dashboards';

export default {
  data() {
    return {
      customDashboards: [],
      customDashboardsError: '',
      customDashboardsLoaded: false,
      loadingCustomDashboards: false,
      searchTerm: '',
      activeCategory: 'all',
      advancedSections,
      quickActions: [
        {
          id: 'create-dashboard',
          title: 'Create Your Dashboard',
          description: 'Build your personalized dashboard',
          category: 'Quick Action',
          icon: 'grid',
          type: 'internal',
          route: '/custom',
          featured: true,
        },
        {
          id: 'ai-dashboard-builder',
          title: 'AI Dashboard Builder',
          description: 'Generate custom dashboards using AI automation',
          category: 'Quick Action',
          icon: 'grid',
          type: 'internal',
          route: '/ai-dashboard-builder',
          featured: true,
        },
      ],
      dashboardCatalog: [
        {
          id: 'health-facilities',
          title: 'Health Facilities',
          description: 'Track and manage health facility data',
          category: 'Health Inputs',
          icon: 'heart',
          type: 'internal',
          route: '/dashboard/Health_Facility',
        },
        {
          id: 'health-financing',
          title: 'Health Financing',
          description: 'Monitor health financing and budgets',
          category: 'Health Inputs',
          icon: 'heart',
          type: 'internal',
          route: '/dashboard/Health_Financing',
        },
        {
          id: 'health-workforce',
          title: 'Health Workforce',
          description: 'Analyze health workforce metrics',
          category: 'Health Inputs',
          icon: 'heart',
          type: 'internal',
          route: '/dashboard/Health_Workforce',
          featured: true,
        },
        {
          id: 'facility-finder',
          title: 'Health Facilities Finder(GIS)- work in progress',
          description: 'Geographic information system for facilities',
          category: 'Health Inputs',
          icon: 'heart',
          type: 'internal',
          route: '/health-facilities-profile',
        },
        {
          id: 'service-access',
          title: 'Health Service Access',
          description: 'Measure service accessibility',
          category: 'Health Outputs',
          icon: 'activity',
          type: 'internal',
          route: '/dashboard/Health_Service_Access',
        },
        {
          id: 'service-uptake-monthly',
          title: 'Health Service Uptake (NHMIS-Monthly)',
          description: 'Monthly health service utilization',
          category: 'Health Outputs',
          icon: 'activity',
          type: 'external',
          route: 'https://msdat.fmohconnect.gov.ng/health_service_uptake/',
        },
        {
          id: 'service-uptake-quarterly',
          title: 'Health Service Uptake (NHMIS-Quarterly)',
          description: 'Quarterly health service trends',
          category: 'Health Outputs',
          icon: 'activity',
          type: 'internal',
          route: '/health-service-uptake',
          featured: true,
        },
        {
          id: 'monthly-nhmis',
          title: 'Monthly NHMIS Insights',
          description: 'Monthly NHMIS data insights',
          category: 'Health Outputs',
          icon: 'activity',
          type: 'external',
          route: 'https://monthly-nhmis-analysis.fmohconnect.gov.ng/',
        },
        {
          id: 'outcomes-coverage',
          title: 'Health Outcomes and Service Coverage',
          description: 'Disease burden and intervention coverage',
          category: 'Health Outcomes',
          icon: 'graph-up',
          type: 'internal',
          route: '/dashboard/Health_Outcomes_and_Service_Coverage',
        },
        {
          id: 'mental-health',
          title: 'Mental Health Dashboard',
          description: 'Mental health indicators and trends',
          category: 'Health Outcomes',
          icon: 'graph-up',
          type: 'internal',
          route: '/dashboard/Mental_Health_Dashboard',
        },
        {
          id: 'quarterly-performance',
          title: 'Quarterly Performance Assessment',
          description: 'Quarterly analytics on state performance',
          category: 'Health Outcomes',
          icon: 'graph-up',
          type: 'internal',
          route: '/dashboard/Quarterly_Performance_Assessment_Dashboard',
        },
        {
          id: 'disease-surveillance',
          title: 'Disease Surveillance',
          description: 'Integrated disease surveillance indicators',
          category: 'Health Outcomes',
          icon: 'graph-up',
          type: 'internal',
          route: '/dashboard/Disease_Surveillance',
        },
        {
          id: 'advanced-triangulation',
          title: 'Advanced Triangulation',
          description: 'Analyze health data from multiple sources',
          category: 'Advanced Analytics',
          icon: 'bezier',
          type: 'internal',
          route: '/advanced-triangulation-dashboard',
        },
        {
          id: 'demographics',
          title: 'Demographics',
          description: 'Population structure, growth, and distribution',
          category: 'Population',
          icon: 'people',
          type: 'internal',
          route: '/dashboard/Demographics',
        },
        {
          id: 'gis-mapping',
          title: 'GIS Mapping',
          description: 'Geospatial population and health visualization',
          category: 'Population',
          icon: 'people',
          type: 'internal',
          route: '/dashboard/GIS_Mapping_Dashboard',
        },
        {
          id: 'ndr1',
          title: 'HIV/AIDs National Repository Dashboard 1',
          description: 'Comprehensive HIV-related data from national systems',
          category: 'Thematic Dashboards',
          icon: 'pie-chart',
          type: 'internal',
          route: '/external-ndr1',
        },
        {
          id: 'ndr2',
          title: 'HIV/AIDs National Repository Dashboard 2',
          description: 'Comprehensive HIV-related data from national systems',
          category: 'Thematic Dashboards',
          icon: 'pie-chart',
          type: 'internal',
          route: '/external-ndr2',
        },
        {
          id: 'ncdc-disease-surveillance',
          title: 'Disease Surveillance (NCDC)',
          description: 'IDSR indicators of relevant diseases',
          category: 'Thematic Dashboards',
          icon: 'pie-chart',
          type: 'internal',
          route: '/external-ncdc',
        },
        {
          id: 'cemonc-dashboard',
          title: 'CEmONC Dashboard',
          description: 'Comprehensive dashboard for CEmONC data',
          category: 'Thematic Dashboards',
          icon: 'pie-chart',
          type: 'internal',
          route: '/cemonc-dashboard',
        },
        {
          id: 'ndhs-tracker',
          title: 'NDHS Tracker Dashboard',
          description: 'Track NDHS indicators and trends over time',
          category: 'Thematic Dashboards',
          icon: 'pie-chart',
          type: 'internal',
          route: '/ndhs-tracker',
        },
        {
          id: 'mpcdr-dashboard',
          title: 'MPCDR Dashboard',
          description: 'Maternal Perinatal and Child Death Review insights',
          category: 'Thematic Dashboards',
          icon: 'pie-chart',
          type: 'internal',
          route: '/mpcdr-dashboard',
        },
        {
          id: 'jar-dashboard',
          title: 'Joint Annual Review Dashboard (JAR)',
          description: 'Joint Annual Review metrics and strategic health outcomes',
          category: 'Other Dashboards',
          icon: 'pie-chart',
          type: 'internal',
          route: '/jar-dashboard',
        },
        {
          id: 'emr-facilities',
          title: 'EMR Facilities Dashboard',
          description:
            'Track and visualize Electronic Medical Record (EMR) implementation and personnel assignment.',
          category: 'Thematic Dashboards',
          icon: 'pie-chart',
          type: 'internal',
          route: '/emr-facilities',
        },
        // {
        //   id: 'emr-dashboard',
        //   title: 'EMR Dashboard',
        //   description: 'Explore EMR dashboard insights and trends.',
        //   category: 'Thematic Dashboards',
        //   icon: 'pie-chart',
        //   type: 'internal',
        //   route: '/emr-dashboard',
        // },
        {
          id: 'health-profiles',
          title: 'Health Profiles',
          description: 'Overview of state or national health status',
          category: 'Other Dashboards',
          icon: 'grid',
          type: 'internal',
          route: '/health-profiles',
        },
        {
          id: 'governors-dashboard',
          title: "Governors' Dashboard",
          description: 'High-level state health insights',
          category: 'Other Dashboards',
          icon: 'grid',
          type: 'external',
          route: 'https://ngf.fmohconnect.gov.ng/',
        },
        {
          id: 'presidential-bond',
          title: 'Scorecard Dashboard of Presidential Bond Indicators',
          description: 'Monitor priority national health commitments',
          category: 'Other Dashboards',
          icon: 'grid',
          type: 'internal',
          route: '/presidential-bond-scorecard',
        },
      ],
    };
  },

  computed: {
    ...mapGetters('AUTH_STORE', ['isAuthenticated']),
    customDashboardCards() {
      if (!this.isAuthenticated) {
        return [];
      }

      return this.customDashboards.map((dashboard) => ({
        id: `custom-${dashboard.id}`,
        title: dashboard.name_of_dashboard,
        description: dashboard.description || 'Open this public custom dashboard.',
        category: 'Custom Dashboards',
        icon: 'person-workspace',
        type: 'internal',
        route: `/custom/public/${dashboard.id}`,
      }));
    },
    advancedAnalyticsCards() {
      return this.advancedSections.map((section, index) => ({
        id: `advanced-${index}`,
        title: section.label,
        description: section.tooltip || 'Explore advanced analytics insights.',
        category: 'Advanced Analytics',
        icon: 'bezier',
        type: 'advanced',
        advancedIndex: index,
      }));
    },
    allDashboards() {
      return [
        ...this.dashboardCatalog,
        ...this.advancedAnalyticsCards,
        ...this.customDashboardCards,
      ];
    },
    categoryFilters() {
      const categories = [
        { key: 'Health Inputs', label: 'Health Inputs' },
        { key: 'Health Outputs', label: 'Health Outputs' },
        { key: 'Health Outcomes', label: 'Health Outcomes' },
        { key: 'Advanced Analytics', label: 'Advanced Analytics' },
        { key: 'Population', label: 'Population' },
        { key: 'Thematic Dashboards', label: 'Thematic Dashboard' },
        { key: 'Other Dashboards', label: 'Other Dashboard' },
      ];

      if (this.isAuthenticated) {
        categories.splice(categories.length - 1, 0, {
          key: 'Custom Dashboards',
          label: 'Custom Dashboards',
        });
      }

      return [
        ...categories.map((category) => ({
          key: category.key,
          label: category.label,
          count: this.allDashboards.filter((dashboard) => dashboard.category === category.key)
            .length,
        })),
        {
          key: 'all',
          label: 'All Dashboards',
          count: this.allDashboards.length,
        },
      ];
    },
    filteredDashboards() {
      const query = this.searchTerm.toLowerCase();

      return this.allDashboards.filter((dashboard) => {
        const matchesCategory
          = this.activeCategory === 'all' || dashboard.category === this.activeCategory;
        const haystack = [dashboard.title, dashboard.category, dashboard.description]
          .join(' ')
          .toLowerCase();

        const matchesSearch = !query || haystack.includes(query);
        return matchesCategory && matchesSearch;
      });
    },
    showCustomDashboardsError() {
      return (
        this.activeCategory === 'Custom Dashboards'
        && this.customDashboardsError
        && this.customDashboardCards.length === 0
      );
    },
    showCustomDashboardsLoading() {
      return this.activeCategory === 'Custom Dashboards' && this.loadingCustomDashboards;
    },
  },

  created() {
    this.restoreCachedCustomDashboards();
  },

  mounted() {
    if (this.isAuthenticated) {
      this.loadCustomDashboards();
    }
  },

  methods: {
    ...mapActions('AUTH_STORE', ['SAVE_DASHBOARDS']),
    emitClose() {
      this.$emit('close');
    },
    restoreCachedCustomDashboards() {
      if (!this.isAuthenticated) {
        return;
      }

      const cachedDashboards = localStorage.getItem(CUSTOM_DASHBOARDS_CACHE_KEY);
      if (!cachedDashboards) {
        return;
      }

      try {
        const parsedDashboards = JSON.parse(cachedDashboards);
        if (Array.isArray(parsedDashboards)) {
          this.customDashboards = parsedDashboards;
          this.customDashboardsLoaded = true;
        }
      } catch (error) {
        localStorage.removeItem(CUSTOM_DASHBOARDS_CACHE_KEY);
      }
    },
    cacheCustomDashboards(dashboards) {
      localStorage.setItem(CUSTOM_DASHBOARDS_CACHE_KEY, JSON.stringify(dashboards));
    },
    async handleFilterClick(categoryKey) {
      if (categoryKey === 'Custom Dashboards' && !this.isAuthenticated) {
        this.activeCategory = 'all';
        return;
      }

      this.activeCategory = categoryKey;

      if (categoryKey === 'Custom Dashboards') {
        await this.loadCustomDashboards();
      }
    },
    async loadCustomDashboards() {
      if (!this.isAuthenticated || this.customDashboardsLoaded || this.loadingCustomDashboards) {
        return;
      }

      this.customDashboardsError = '';
      this.loadingCustomDashboards = true;

      try {
        const response = await this.SAVE_DASHBOARDS();
        const results = response?.data?.results || [];
        this.customDashboards = Object.values(results).filter((item) => item.is_private === false);
        this.cacheCustomDashboards(this.customDashboards);
        this.customDashboardsLoaded = true;
      } catch (error) {
        this.customDashboardsError = 'Unable to load custom dashboards.';
      } finally {
        this.loadingCustomDashboards = false;
      }
    },
    openAdvancedAnalytics(index, label) {
      console.log(`Opening Advanced Analytics for: ${label} (index ${index})`);

      const relativeUrl = this.$router.resolve({
        path: '/dashboard/Advanced_Analytics',
        query: { index },
      }).href;

      const fullUrl = `${window.location.origin}${relativeUrl}`;

      localStorage.setItem('advanced_url', fullUrl);
      window.open(fullUrl, '_blank', 'noopener');
    },
    openInNewTab(url) {
      window.open(url, '_blank', 'noopener');
    },
    openDashboardCard(dashboard) {
      this.$emit('close');

      if (dashboard.type === 'advanced') {
        this.openAdvancedAnalytics(dashboard.advancedIndex, dashboard.title);
        return;
      }

      if (dashboard.type === 'external') {
        this.openInNewTab(dashboard.route);
        return;
      }

      const resolved = this.$router.resolve({ path: dashboard.route });
      this.openInNewTab(resolved.href);
    },
  },

  watch: {
    isAuthenticated(isAuthenticated) {
      if (!isAuthenticated && this.activeCategory === 'Custom Dashboards') {
        this.activeCategory = 'all';
        this.customDashboards = [];
        this.customDashboardsLoaded = false;
        localStorage.removeItem(CUSTOM_DASHBOARDS_CACHE_KEY);
        return;
      }

      if (isAuthenticated && !this.customDashboardsLoaded) {
        this.restoreCachedCustomDashboards();
        this.loadCustomDashboards();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.drop-card-shell {
  position: relative;
}

.drop-card-panel {
  position: fixed;
  top: 6rem;
  left: 1rem;
  right: 1rem;
  z-index: 6;
  width: auto;
  max-height: 62vh;
  padding: 12px;
  border-radius: 0;
  background: linear-gradient(180deg, #fcfcfb 0%, #f7f7f6 100%);
  border: 1px solid #d8d8d2;
  box-shadow: 0 24px 60px rgba(15, 30, 35, 0.18);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.drop-card-header {
  padding: 0 2px 12px;
}

.drop-card-header__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.drop-card-header__title {
  margin: 0 0 10px;
  color: #6c757f;
  font-size: 11px;
  line-height: 1.4;
}

.drop-card-close {
  width: 28px;
  height: 28px;
  border: 1px solid #dce2e8;
  border-radius: 999px;
  background: #ffffff;
  color: #52606d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.drop-card-close:hover,
.drop-card-close:focus {
  background: #f2f7f8;
  color: #1f2d3a;
  border-color: #bfcad4;
  outline: none;
}

.drop-card-search {
  position: relative;
}

.drop-card-search__icon {
  position: absolute;
  top: 50%;
  left: 12px;
  transform: translateY(-50%);
  color: #9ea6b2;
  font-size: 12px;
}

.drop-card-search__input {
  width: 100%;
  height: 34px;
  border: 1px solid #eceef1;
  border-radius: 7px;
  background: #f2f4f7;
  padding: 0 14px 0 34px;
  color: #36414a;
  font-size: 11px;
  outline: none;
}

.drop-card-search__input::placeholder {
  color: #9da5b1;
}

.drop-card-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0 2px 14px;
}

.drop-card-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  color: #313a45;
  font-size: 10px;
  font-weight: 500;
  transition: all 0.18s ease;
}

.drop-card-filter--cta {
  border-color: rgba(52, 132, 129, 0.22);
  background: linear-gradient(180deg, rgba(52, 132, 129, 0.12) 0%, rgba(52, 132, 129, 0.06) 100%);
  color: #1f4f52;
  font-weight: 700;
}

.drop-card-filter--cta:hover,
.drop-card-filter--cta:focus {
  border-color: rgba(52, 132, 129, 0.4);
  box-shadow: 0 8px 16px rgba(16, 68, 77, 0.08);
  outline: none;
}

.drop-card-filter__count {
  min-width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #eceef2;
  color: #5a6470;
  font-size: 9px;
  font-weight: 700;
}

.drop-card-filter__count--cta {
  min-width: 18px;
  background: rgba(255, 255, 255, 0.6);
  color: #1f6f6b;
}

.drop-card-filter--active {
  background: #348481;
  color: #ffffff;
}

.drop-card-filter--active .drop-card-filter__count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.drop-card-state {
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #5e6772;
  font-size: 12px;
}

.drop-card-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 2px 2px 0;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.drop-card-state--empty {
  flex-direction: column;
  gap: 4px;
}

.drop-card-state__title {
  margin: 0;
  color: #26313b;
  font-size: 14px;
  font-weight: 600;
}

.drop-card-state__text {
  margin: 0;
  font-size: 11px;
}

.drop-card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  padding: 2px 4px 4px 2px;
}

.drop-card-item {
  min-height: 60px;
  padding: 6px 9px 8px;
  border: 1.5px solid #348481;
  border-radius: 6px;
  background: linear-gradient(180deg, #ffffff 0%, #fcfcfb 100%);
  cursor: pointer;
  outline: none;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.drop-card-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(16, 68, 77, 0.12);
}

.drop-card-item:focus {
  border-color: #0f9f9d;
  box-shadow: 0 0 0 2px rgba(23, 183, 181, 0.18);
}

.drop-card-item--featured .drop-card-item__star {
  color: #f8bc16;
}

.drop-card-item__top,
.drop-card-item__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.drop-card-item__icon {
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #12bebc;
  font-size: 12px;
}

.drop-card-item__star,
.drop-card-item__info {
  font-size: 10px;
}

.drop-card-item__info {
  color: #7f8a97;
}

.drop-card-item__body {
  padding: 6px 0 5px;
}

.drop-card-item__title {
  margin: 0 0 2px;
  color: #000;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.25;
}

.drop-card-item__description {
  margin: 0;
  color: #7a838f;
  font-size: 12px;
  line-height: 1.2;
}

.drop-card-item__tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 0px;
  border-radius: 999px;
  background: #f6f7f9;
  color: #2e363f;
  font-size: 9px;
  font-weight: 500;
  color: #348481;
}

.drop-card-item__arrow {
  color: #99a2ad;
  font-size: 12px;
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: 5;
  background: rgba(84, 89, 97, 0.18);
}
</style>
